/**
 * Video languages for the create-video Advanced settings "Language" picker.
 *
 * When the user picks a language, the script Director writes the narration,
 * dialog and on-screen overlay text in it, the voiceover TTS speaks it, and
 * the Whisper caption pass transcribes with the matching ISO-639-1 hint.
 * 'auto' lets the Director follow the prompt's own language and self-report
 * the code it used.
 *
 * `code` is the ISO-639-1 code Whisper expects as its `language` hint.
 */

export interface VideoLanguageOption {
  code: string;
  label: string;
}

// Curated subset of Whisper-supported languages, pre-sorted by label for the
// picker dropdown. Codes are ISO-639-1.
export const VIDEO_LANGUAGE_OPTIONS: VideoLanguageOption[] = [
  { code: 'ar', label: 'Arabic' },
  { code: 'zh', label: 'Chinese' },
  { code: 'cs', label: 'Czech' },
  { code: 'da', label: 'Danish' },
  { code: 'nl', label: 'Dutch' },
  { code: 'en', label: 'English' },
  { code: 'tl', label: 'Filipino' },
  { code: 'fi', label: 'Finnish' },
  { code: 'fr', label: 'French' },
  { code: 'de', label: 'German' },
  { code: 'el', label: 'Greek' },
  { code: 'he', label: 'Hebrew' },
  { code: 'hi', label: 'Hindi' },
  { code: 'hu', label: 'Hungarian' },
  { code: 'id', label: 'Indonesian' },
  { code: 'it', label: 'Italian' },
  { code: 'ja', label: 'Japanese' },
  { code: 'ko', label: 'Korean' },
  { code: 'ms', label: 'Malay' },
  { code: 'no', label: 'Norwegian' },
  { code: 'pl', label: 'Polish' },
  { code: 'pt', label: 'Portuguese' },
  { code: 'ro', label: 'Romanian' },
  { code: 'ru', label: 'Russian' },
  { code: 'es', label: 'Spanish' },
  { code: 'sv', label: 'Swedish' },
  { code: 'th', label: 'Thai' },
  { code: 'tr', label: 'Turkish' },
  { code: 'uk', label: 'Ukrainian' },
  { code: 'vi', label: 'Vietnamese' },
];

/** Sentinel for "follow the prompt language" — the picker default. */
export const AUTO_VIDEO_LANGUAGE = 'auto';

const OPTION_BY_CODE = new Map(
  VIDEO_LANGUAGE_OPTIONS.map((option) => [option.code, option] as const),
);

export function isSupportedVideoLanguage(code: string): boolean {
  return OPTION_BY_CODE.has(code);
}

/** Human-readable name for a code ("ko" → "Korean"). */
export function videoLanguageLabel(code: string): string | undefined {
  return OPTION_BY_CODE.get(code)?.label;
}

/**
 * Maps a loose value — an ISO code, a regional / script tag, a full
 * language name, or a known alias — to one of the picker-supported
 * ISO-639-1 codes, or null when it can't be resolved. Used to sanitize the
 * API request value (the explicit Advanced-settings pick). For the
 * Director's self-reported language use normalizeWhisperLanguage, which
 * accepts the wider set Whisper can transcribe.
 */
export function normalizeVideoLanguageCode(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  const value = raw.trim().toLowerCase();
  if (!value) return null;

  // Full names / endonyms / ISO-639-2 codes with no matching ISO-639-1 code.
  const aliases: Record<string, string> = {
    mandarin: 'zh',
    cantonese: 'zh',
    'chinese (simplified)': 'zh',
    'chinese (traditional)': 'zh',
    castilian: 'es',
    'brazilian portuguese': 'pt',
    filipino: 'tl',
    tagalog: 'tl',
    fil: 'tl',
    'english (uk)': 'en',
    'english (us)': 'en',
  };

  const resolve = (token: string): string | null => {
    if (!token) return null;
    if (OPTION_BY_CODE.has(token)) return token;
    const byLabel = VIDEO_LANGUAGE_OPTIONS.find(
      (option) => option.label.toLowerCase() === token,
    );
    if (byLabel) return byLabel.code;
    return aliases[token] ?? null;
  };

  // Try the value as-is, then again with any regional / script subtag
  // dropped so BCP-47 tags collapse to their base code (pt-BR → pt,
  // zh-Hant → zh, es-419 → es, en_US → en).
  return resolve(value) ?? resolve(value.split(/[-_]/)[0]);
}

/**
 * ISO-639-1 codes Whisper can transcribe. Deliberately broader than
 * VIDEO_LANGUAGE_OPTIONS (the curated picker): in Auto mode the script
 * Director follows the prompt's own language, which may be any of these,
 * so its self-reported language must be validated against the full set.
 */
export const WHISPER_LANGUAGE_CODES: ReadonlySet<string> = new Set([
  'af',
  'am',
  'ar',
  'as',
  'az',
  'ba',
  'be',
  'bg',
  'bn',
  'bo',
  'br',
  'bs',
  'ca',
  'cs',
  'cy',
  'da',
  'de',
  'el',
  'en',
  'es',
  'et',
  'eu',
  'fa',
  'fi',
  'fo',
  'fr',
  'gl',
  'gu',
  'ha',
  'haw',
  'he',
  'hi',
  'hr',
  'ht',
  'hu',
  'hy',
  'id',
  'is',
  'it',
  'ja',
  'jw',
  'ka',
  'kk',
  'km',
  'kn',
  'ko',
  'la',
  'lb',
  'ln',
  'lo',
  'lt',
  'lv',
  'mg',
  'mi',
  'mk',
  'ml',
  'mn',
  'mr',
  'ms',
  'mt',
  'my',
  'ne',
  'nl',
  'nn',
  'no',
  'oc',
  'pa',
  'pl',
  'ps',
  'pt',
  'ro',
  'ru',
  'sa',
  'sd',
  'si',
  'sk',
  'sl',
  'sn',
  'so',
  'sq',
  'sr',
  'su',
  'sv',
  'sw',
  'ta',
  'te',
  'tg',
  'th',
  'tk',
  'tl',
  'tr',
  'tt',
  'uk',
  'ur',
  'uz',
  'vi',
  'yi',
  'yo',
  'yue',
  'zh',
]);

/**
 * Resolves a language hint for Whisper transcription from a loose value
 * (the Director's self-reported captionLanguage). Reuses
 * normalizeVideoLanguageCode for names / aliases / regional tags, then
 * accepts any bare ISO-639-1 code Whisper supports even when it isn't in
 * the curated picker list (e.g. bn, sw, ur). Returns null when nothing
 * usable can be derived — callers should then let Whisper auto-detect
 * rather than guessing a wrong language.
 */
export function normalizeWhisperLanguage(raw: unknown): string | null {
  const mapped = normalizeVideoLanguageCode(raw);
  if (mapped) return mapped;
  if (typeof raw !== 'string') return null;
  const base = raw.trim().toLowerCase().split(/[-_]/)[0];
  return WHISPER_LANGUAGE_CODES.has(base) ? base : null;
}
